import React, { useState } from "react";
// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { th, enUS } from "date-fns/locale";
import { motion } from "framer-motion";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Search, FileText, CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const statusConfig = {
  completed: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700" },
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700" },
  processing: { icon: Loader2, color: "bg-blue-100 text-blue-700" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700" },
};

export default function ResellerSettlements() {
  const { t, language } = useLanguage();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const resellerName = "Asia Gaming Network";

  // TODO: Implement custom API calls
  const { data: settlements = [], isLoading } = useQuery({
    queryKey: ["settlements"],
    queryFn: async () => [], // TODO: Implement Settlement.list API call
  });

  const filteredSettlements = settlements.filter(s => {
    if (search && !s.settlement_id?.toLowerCase().includes(search.toLowerCase()) &&
      !s.merchant_code?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    return true;
  });

  const totalAmount = filteredSettlements.filter(s => s.status === "completed").reduce((sum, s) => sum + (s.net_amount || 0), 0);

  return (
    <ResellerLayout currentPage="ResellerSettlements" resellerName={resellerName}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t("settlements")}</h1>
          <p className="text-slate-500">{t("totalSettled")}: RS{totalAmount.toLocaleString()}</p>
        </div>

        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder={t("search") + "..."} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder={t("status")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("allStatus")}</SelectItem>
                <SelectItem value="completed">{t("completed")}</SelectItem>
                <SelectItem value="pending">{t("pending")}</SelectItem>
                <SelectItem value="processing">{t("processing")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("settlement")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">{t("merchant")}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("amount")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("status")}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">{t("period")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  <tr><td colSpan={5} className="text-center py-12 text-slate-500">{t("loading")}</td></tr>
                ) : filteredSettlements.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-12 text-slate-500">{t("noSettlements")}</td></tr>
                ) : (
                  filteredSettlements.map((s, i) => {
                    const status = statusConfig[s.status] || statusConfig.pending;
                    const StatusIcon = status.icon;
                    return (
                      <motion.tr
                        key={s.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.02 }}
                        className="hover:bg-slate-50"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
                              <FileText className="h-4 w-4 text-violet-600" />
                            </div>
                            <div>
                              <p className="font-mono text-sm">{s.settlement_id}</p>
                              <p className="text-xs text-slate-400 sm:hidden">{s.merchant_code}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell text-sm">{s.merchant_code || t("nA")}</td>
                        <td className="px-4 py-3 text-right font-semibold">RS{s.net_amount?.toLocaleString()}</td>
                        <td className="px-4 py-3">
                          <Badge className={`${status.color} gap-1 border-0`}>
                            <StatusIcon className={`h-3 w-3 ${s.status === "processing" ? "animate-spin" : ""}`} />
                            <span className="hidden sm:inline">{t(s.status)}</span>
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500 hidden md:table-cell">
                          {s.period_start && s.period_end ? (
                            `${format(new Date(s.period_start), language === "th" ? "d MMM" : "MMM d", { locale: language === "th" ? th : enUS })} - ${format(new Date(s.period_end), language === "th" ? "d MMM" : "MMM d", { locale: language === "th" ? th : enUS })}`
                          ) : t("nA")}
                        </td>
                      </motion.tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ResellerLayout>
  );
}